import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { useNavigate } from 'react-router-dom';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import pl from 'date-fns/locale/pl'; // <-- import Polish
import React, { useMemo } from 'react';
import ReservationCalendarTitle from './ReservationCalendarTitle';
import { stayStatusMapping } from '../StayStatusIcon';
import CustomTimeComponent from './CustomTimeComponent';
import { useGlobalConfig } from '../../globalConfig/GlobalConfigContext';
import { FaRegCircle } from 'react-icons/fa';
import { getBlockedDaySourceLabel, isBookingBlock } from '../../../helpers/bookingCalendarUtils';

const messages = {
  allDay: 'Cały dzień',
  previous: 'Poprzedni',
  next: 'Następny',
  today: 'Bieżący',
  month: 'Miesiąc',
  week: 'Tydzień',
  day: 'Dzień',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Godzina',
  event: 'Wydarzenie',
  noEventsInRange: 'Brak wydarzeń w tym zakresie',
};

// Set locales
const locales = {
  pl: pl, // <-- use Polish here
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), // Monday as first day
  getDay,
  locales,
});

const BigCalendar = ({ reservations, blocked, views, height = '90vh' }) => {
  const navigate = useNavigate();
  const { checkinTime, checkoutTime } = useGlobalConfig();

  const events = reservations.map((reservation) => {
    const inTime = reservation.checkinTime || checkinTime;
    const outTime = reservation.checkoutTime || checkoutTime;

    const [startHour, startMinute] = inTime.split(':').map(Number);
    const [endHour, endMinute] = outTime.split(':').map(Number);

    const startDate = new Date(reservation.startDate);
    const endDate = new Date(reservation.endDate);

    const startDateTime = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startHour,
      startMinute,
      0,
    );

    const endDateTime = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endHour,
      endMinute,
      0,
    );

    return {
      title: (
        <ReservationCalendarTitle
          key={reservation.businessId}
          reservation={reservation}
        />
      ),
      start: startDateTime,
      end: endDateTime,
      reservationId: reservation.id,
      stayStatus: reservation.stayStatus,
      paymentStatus: reservation.paymentStatus,
      allDay: false,
    };
  });

  blocked.forEach((bd) => {
    const inTime = checkinTime;
    const outTime = checkoutTime;

    const [startHour, startMinute] = inTime.split(':').map(Number);
    const [endHour, endMinute] = outTime.split(':').map(Number);

    const startDate = new Date(bd.startDate);
    const endDate = new Date(bd.endDate);

    const startDateTime = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startHour,
      startMinute,
      0,
    );

    const endDateTime = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endHour,
      endMinute,
      0,
    );

    const sourceLabel = getBlockedDaySourceLabel(bd);
    const bookingBlock = isBookingBlock(bd);

    const bookingTitle = bd.house?.name
      ? `Blokada Booking - ${bd.house.name}`
      : 'Blokada Booking';

    events.push({
      title: bookingBlock
        ? bookingTitle
        : 'Blokada: ' +
          bd.house?.name +
          ' ' +
          (bd.title || sourceLabel),
      start: startDateTime,
      end: endDateTime,
      blocked: true,
      blockedLabel: sourceLabel,
      blockedTitle: bookingBlock ? null : bd.title,
      bookingTitle,
      isBookingBlock: bookingBlock,
      allDay: false,
    });
  });

  const eventStyleGetter = (event) => {
    const statusInfo = event.blocked
      ? event.isBookingBlock
        ? {
            iconColor: '#1d4ed8',
            color: '#1d4ed8',
            bg: '#eff6ff',
            icon: FaRegCircle,
            borderColor: '#1d4ed8',
          }
        : {
            iconColor: '#ffc107',
            color: 'rgb(55, 65, 81)',
            bg: '#ffffff',
            icon: FaRegCircle,
            borderColor: 'rgb(55, 65, 81)',
          }
      : stayStatusMapping[event.stayStatus];

    return {
      style: {
        backgroundColor: statusInfo?.bg,
        color: statusInfo?.color,
        borderRadius: '1rem',
        border: statusInfo?.borderColor ? '1px solid' : 'none',
        borderColor: statusInfo?.borderColor || 'transparent',
      },
    };
  };

  const onSelectEvent = (event) => {
    if (event.blocked) {
      return;
    }

    navigate(`/reservation/${event.reservationId}`);
  };

  const { formats } = useMemo(
    () => ({
      formats: {
        eventTimeRangeEndFormat: (date, culture, localizer) => {
          return ' do ' + localizer.format(date.end, 'HH:mm', culture);
        },
        eventTimeRangeStartFormat: (date, culture, localizer) => {
          return ' od ' + localizer.format(date.start, 'HH:mm', culture);
        },
      },
    }),
    [],
  );

  return (
    <div style={{ height: height }}>
      <Calendar
        culture="pl"
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        eventPropGetter={eventStyleGetter}
        onSelectEvent={onSelectEvent}
        popup={true}
        formats={formats}
        tooltipAccessor={(event) => {
          if (event.blocked) {
            if (event.isBookingBlock) {
              return event.bookingTitle;
            }

            const details = [event.blockedLabel, event.blockedTitle]
              .filter(Boolean)
              .join(' • ');

            return details || 'Blokada';
          }

          return 'Kliknij żeby otworzyć';
        }}
        messages={messages}
        views={views || ['month', 'week', 'day', 'agenda']}
        style={{ height: '100%' }}
        allDay={false}
        showMultiDayTimes={true}
      />
    </div>
  );
};

export default BigCalendar;
